<script setup lang="ts">
	import { computed, ref } from 'vue'
	import { Collapse } from 'vue-collapsed'

	import StringToggle from '@/components/StringToggle.vue'

	import uppercaseFirst from '@/composables/uppercaseFirst'
	import useAuthUser from '@/composables/useAuthUser'
	import useFixedNumber from '@/composables/useFixedNumber'

	import { CancellationPolicy } from '../interfaces/hotelRate'
	import { formatProviderDeadline } from '../utils/hotelDates'
	import CancellationDeadline from './CancellationDeadline.vue'

	const isExpanded = ref(true)
	const { user } = useAuthUser()
	const internal = computed(() => user.value?.internal)

	interface Props {
		cancellationPolicy: CancellationPolicy[]
		className?: string
		hasRestrictions: boolean
		paymentModel?: string
	}

	const props = withDefaults(defineProps<Props>(), {
		className: '',
	})

	const cancellationPolicies = computed(() => {
		return props.cancellationPolicy?.map(
			({ description, amount, providerAmount, providerCurrency, ...rest }) => ({
				...rest,
				amount: amount && `$${useFixedNumber(amount).fixedNumber} MXN`,
				// Hotelbeds (HBX 3.8): el cargo en la moneda del hotel, sin tocar. Sólo llega
				// para personal interno: es lo que HBX nos cobra, no lo que pierde el cliente.
				providerCharge:
					providerAmount && providerCurrency ?
						`${useFixedNumber(providerAmount).fixedNumber} ${providerCurrency}`
					:	null,
				description: description?.toLocaleLowerCase() || '',
			})
		)
	})

	// Hotelbeds (HBX 3.8, #954): primero el plazo sin costo, tal cual y con la hora del
	// hotel. Después de ese plazo cancela un asesor, así que el cliente no ve montos.
	const hotelbedsPolicies = computed(
		() =>
			cancellationPolicies.value
				?.filter((policy) => policy.from)
				.sort((a, b) => Date.parse(a.from!) - Date.parse(b.from!)) ?? []
	)
	const hotelbedsFreeUntil = computed(() => {
		const first = hotelbedsPolicies.value[0]
		// `deadline` llega en null cuando el primer cargo ya corre.
		return first?.deadline ? formatProviderDeadline(first.from) : null
	})
	const otherPolicies = computed(
		() => cancellationPolicies.value?.filter((policy) => !policy.from) ?? []
	)
</script>

<template>
	<div
		v-if="cancellationPolicies?.length > 0 || hasRestrictions"
		:class="[props.className, 'h-fit transition-all']"
	>
		<div class="flex flex-col">
			<button
				class="disclosure-button !bg-red-50"
				@click="isExpanded = !isExpanded"
			>
				<p class="text-lg font-bold text-red-600">Políticas de cancelación</p>

				<span
					:class="[
						'material-symbols-outlined text-red-600',
						isExpanded && 'rotate-180',
					]"
				>
					expand_more
				</span>
			</button>

			<Collapse
				:when="isExpanded"
				class="v-collapse"
			>
				<!-- Mensaje cuando solo hay restricciones sin políticas -->
				<div
					v-if="
						hasRestrictions &&
						(!cancellationPolicies || cancellationPolicies.length === 0)
					"
					class="mt-4 p-3 sm:p-4"
				>
					<div class="flex flex-col gap-2">
						<div class="flex items-start gap-2">
							<span
								class="material-symbols-outlined text-red-600 text-lg sm:text-xl flex-shrink-0"
							>
								block
							</span>
							<div class="flex flex-col gap-1">
								<p class="text-sm sm:text-base font-bold text-red-600">
									Tarifa no reembolsable
								</p>
								<p class="text-xs sm:text-sm text-red-700 leading-relaxed">
									Una vez confirmada esta reserva, ya no se aceptarán cambios o
									cancelaciones.
								</p>
							</div>
						</div>
					</div>
				</div>

				<!-- Hotelbeds (HBX 3.8, #954): el plazo sin costo tal cual, en la hora del hotel.
				Los cargos de HBX son nuestro costo: sólo los ve el personal interno. -->
				<div
					v-if="hotelbedsPolicies.length"
					class="mt-4 p-1 grid gap-2"
				>
					<div class="flex flex-wrap justify-between gap-2">
						<p class="w-fit text-sm font-semibold text-gray-600">
							Cancelación sin costo hasta:
						</p>
						<p
							:class="[
								'w-fit text-sm',
								hotelbedsFreeUntil ? 'text-gray-500' : 'text-red-500 font-bold',
							]"
						>
							{{ hotelbedsFreeUntil ?? 'El plazo sin costo ya venció.' }}
						</p>
					</div>
					<p class="text-xs text-gray-500">
						Después de esa fecha, la cancelación se hace con un asesor y puede
						tener cargo.
					</p>

					<template v-if="internal">
						<div
							v-for="(policy, index) in hotelbedsPolicies.filter(
								(p) => p.providerCharge
							)"
							:key="index"
							class="flex flex-wrap justify-between gap-2 border-t pt-2"
						>
							<p class="w-fit text-xs font-semibold text-gray-600">
								Cargo de HBX desde {{ formatProviderDeadline(policy.from) }}:
							</p>
							<p class="w-fit text-xs font-semibold text-gray-500">
								{{ policy.providerCharge }}
								<span
									v-if="policy.amount"
									class="font-normal"
									>(≈ {{ policy.amount }})</span
								>
							</p>
						</div>
					</template>
				</div>

				<div
					v-for="(policy, index) in otherPolicies"
					:key="index"
					class="mt-4 p-1 grid gap-2 rounded-sm border-b pb-2 last-of-type:border-0"
				>
					<div
						v-if="policy.description"
						class="border-b-2 pb-1 border-gray-400"
					>
						<StringToggle
							:text="uppercaseFirst(policy.description)"
							:initiallly-visible="false"
							closed-text="Ver descripción original"
						/>
					</div>

					<CancellationDeadline
						:deadline="policy.deadline"
						:internal-deadline="policy.internalDeadline"
						:is-internal="internal"
					/>

					<div
						v-if="policy.amount && (internal || paymentModel === 'prepaid')"
						class="flex flex-wrap justify-between gap-2"
					>
						<p class="w-fit text-sm font-semibold text-gray-600">
							Costo por cancelación{{
								policy.deadline ? ' fuera de tiempo' : ''
							}}:
						</p>
						<p class="w-fit text-sm font-semibold text-gray-500">
							{{ policy.amount }}
						</p>
					</div>
				</div>
			</Collapse>
		</div>
	</div>
</template>

<style scoped lang="css">
	.disclosure-button {
		@apply flex w-full items-center justify-between rounded-md border bg-gray-50 p-2 text-left font-semibold text-gray-700;
	}
	.v-collapse {
		transition: height 300ms cubic-bezier(0.33, 1, 0.68, 1);
	}
</style>
